<script lang="ts" setup>
import { computed, ref } from 'vue';
import { Dropdown } from 'bootstrap';
import type { Directive } from 'vue';
import ServiceEditor from './ServiceEditor.vue';
import RunTransactionExplorer from './RunTransactionExplorer.vue';
import StatusCountChips from './StatusCountChips.vue';
import { useClimbingNumber } from '../composables/useClimbingNumber';
import RunCostRows from './RunCostRows.vue';
import { serviceTypeDisplay, type Service, type ServiceNodeData } from '@/types';
import { finishedWithNoTransactions, getRunStatusLabel, isStillCounting, serviceCountChips } from '../utils/runFormatters';

// Cards are plain siblings in the stack, so a later card paints over an earlier
// one's dropdown when the menu drops into its space. Raise this card above its
// siblings while its menu is open so the menu is never hidden behind the card below.
const menuOpen = ref(false);


// Close every open "more options" menu except optionally one to keep open.
// Clicks inside an expanded panel are stopped before reaching Bootstrap's document
// listener, so the panel calls this to close menus itself.
function closeOpenMenus(keep?: HTMLElement) {
  document
    .querySelectorAll<HTMLElement>('[data-bs-toggle="dropdown"][aria-expanded="true"]')
    .forEach((el) => {
      if (el !== keep) Dropdown.getInstance(el)?.hide();
    });
}

const vDropdownMenu: Directive<HTMLElement> = {
  mounted(el) {
    // Close any other card's open menu first — two raised cards tie on z-index
    // and the later one would paint over this menu.
    el.addEventListener('show.bs.dropdown', () => {
      closeOpenMenus(el);
      menuOpen.value = true;
    });
    el.addEventListener('hide.bs.dropdown', () => (menuOpen.value = false));
  },
};

const props = defineProps<{
  node: {
    id: string;
    data: ServiceNodeData;
  };
  depth: number;
  isExpanded: boolean;
  children: string[];
  hasMultipleParents: boolean;
  parentCount: number;
  parentNodes?: any[];
  /** Resolved Service object (dirty-overlaid by the parent). Required for editor mode. */
  service?: Service | null;
  /** Whether this service has unsaved edits in the parent's dirty map. */
  isDirty?: boolean;
  /** History mode: the selected run's id and owning workflow id, used to load this service's
   *  transactions (#6009) in the expanded body. */
  runId?: string;
  runWorkflowId?: string;
  /** Flatten the hierarchy: no left indent, so a deeply-nested chain gets full card width. The
   *  hierarchy lines are hidden by the parent view when this is on. */
  flat?: boolean;
}>();

const emit = defineEmits<{
  toggleExpand: [];
  nodeClick: [nodeId: string, nodeData: any];
  addService: [parentNodeId: string];
  runService: [nodeId: string];
  toggleDisabled: [nodeId: string];
  deleteService: [nodeId: string];
  renameService: [nodeId: string];
  duplicateService: [nodeId: string];
  editParents: [nodeId: string];
  serviceUpdate: [nodeId: string, service: Service];
  serviceSave: [nodeId: string];
  serviceDiscard: [nodeId: string];
  toggleComponent: [nodeId: string, isComponent: boolean];
}>();

// Per-type icon from the single service-type display source, so the list view, node cards, and the
// editor header all stay in sync (same resolution as ServiceNode).
const serviceIcon = computed(() => serviceTypeDisplay(props.node.data.serviceType).icon);

// Status class
// Run-status icon colour — the shared .run-status-* palette (styles.scss) so the icon matches
// the run-history status badge rather than Bootstrap's vivid text-* tokens.
const statusClass = computed(() => {
  switch (props.node.data.run?.status) {
    case 'Succeeded': return 'run-status-succeeded';
    case 'Failed':    return 'run-status-failed';
    case 'Warning':   return 'run-status-warning';
    case 'Running':   return 'run-status-running';
    case 'Pending':   return 'run-status-pending';
    default:          return '';
  }
});

// Status icon
const statusIcon = computed(() => {
  switch (props.node.data.run?.status) {
    case 'Succeeded': return 'bi-check-circle';
    case 'Failed':    return 'bi-x-circle';
    case 'Warning':   return 'bi-exclamation-triangle';
    case 'Running':   return 'bi-arrow-repeat';
    case 'Pending':   return 'bi-clock';
    default:          return '';
  }
});

// Duration shown bottom-right in history mode. Current executionNote format is
// "completed in X.Xs" — strip the prefix and just show the duration.
const runDuration = computed(() => {
  const note = props.node.data.executionNote;
  if (!note) return '';
  return note.replace(/^completed in\s+/i, '');
});

// Per-service transaction outcome tallies (history mode). Zero-count chips are dropped by StatusCountChips.
const stillCounting = computed(() => {
  const r = props.node.data.run;
  return !!r && isStillCounting(r);
});

// What a service still creating is counting towards. Zero once it has finished — its own total is then the
// answer this was estimating — and zero on a run that finished before there was a projection to record.
const expected = computed(() => (stillCounting.value ? (props.node.data.run?.expected ?? 0) : 0));

// One climbing figure behind both the fraction and the bar, so the two cannot disagree.
const done = useClimbingNumber(
  () => props.node.data.run?.total ?? 0,
  () => stillCounting.value
);

// Capped, because the projection can come in under: a service whose later parents fan out wider than its
// first chunk did passes its own estimate, and a bar past full says less than a full one does.
const percentDone = computed(() =>
  expected.value ? Math.min(100, (done.value / expected.value) * 100) : 0
);

// Fixed 20px per depth level (plus one level of base offset so even depth 0 clears its lead-in line).
// Kept uniform so the hierarchy rails stay evenly spaced — a deep chain reclaims width via the flatten
// toggle, not a taper (a taper left the measured rails drifting past their parents). Flatten mode drops
// the indent entirely for full-width cards.
const leftIndent = computed(() => (props.flat ? 0 : (props.depth + 1) * 20));
const cardStyle = computed(() => ({
  marginLeft: `${leftIndent.value}px`,
  '--depth': props.depth,
}));

// Card classes
const cardClasses = computed(() => ({
  'stacked-service-card-disabled': props.node.data.disabled,
  'stacked-service-card-expanded': props.isExpanded,
  'stacked-service-card-dirty': props.isDirty,
}));

function handleCardClick() {
  emit('toggleExpand');
}

function handleToggleExpand(event: Event) {
  event.stopPropagation();
  emit('toggleExpand');
}

function handleAddService(event: Event) {
  event.stopPropagation();
  emit('addService', props.node.id);
}

function handleRunService(event: Event) {
  event.stopPropagation();
  emit('runService', props.node.id);
}

function handleToggleDisabled(event: Event) {
  event.stopPropagation();
  emit('toggleDisabled', props.node.id);
}

function handleDeleteService(event: Event) {
  event.stopPropagation();
  emit('deleteService', props.node.id);
}

function handleRenameService(event: Event) {
  event.stopPropagation();
  emit('renameService', props.node.id);
}

function handleDuplicateService(event: Event) {
  event.stopPropagation();
  emit('duplicateService', props.node.id);
}

function handleEditParents(event: Event) {
  event.stopPropagation();
  emit('editParents', props.node.id);
}

function handleEditorUpdate(svc: Service) {
  emit('serviceUpdate', props.node.id, svc);
}

function handleEditorSave() {
  emit('serviceSave', props.node.id);
}

function handleEditorDiscard() {
  emit('serviceDiscard', props.node.id);
}

function handleEditorRun() {
  emit('runService', props.node.id);
}

function handleEditorToggleComponent(_serviceId: string, isComponent: boolean) {
  emit('toggleComponent', props.node.id, isComponent);
}
</script>

<template>
  <div
    class="stacked-service-wrapper"
    :class="{ 'menu-open': menuOpen }"
    :style="cardStyle"
    :data-depth="depth"
    :data-has-children="children.length > 0"
  >
    <div
      class="stacked-service-card card bb-builder-panel"
      :class="cardClasses"
      :data-node-id="node.id"
    >

    <!-- Card header (always visible) -->
    <div class="card-header bg-transparent border-0 p-3" @click="handleCardClick">
      <div class="d-flex align-items-center gap-2">
        <!-- Expand/collapse chevron -->
        <button
          class="btn btn-sm btn-link p-0 text-decoration-none text-body expand-btn"
          @click="handleToggleExpand"
          :title="isExpanded ? 'Collapse' : 'Expand'"
        >
          <i
            class="bi"
            :class="isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"
          ></i>
        </button>

        <!-- Service icon -->
        <div class="service-icon bg-body-tertiary border rounded">
          <i :class="serviceIcon" class="text-secondary"></i>
        </div>

        <!-- Title and description -->
        <div class="flex-grow-1 min-w-0">
          <div class="d-flex align-items-center gap-2 mb-1">
            <h4 class="mb-0 fw-semibold">{{ node.data.label }}</h4>

            <!-- Dirty indicator -->
            <span
              v-if="isDirty"
              class="badge bg-warning-subtle text-warning-emphasis"
              title="Unsaved changes"
            >
              <i class="bi bi-pencil me-1"></i>Unsaved
            </span>

            <!-- Multiple parents badge -->
            <span
              v-if="hasMultipleParents"
              class="badge bg-info-subtle text-info-emphasis"
              :title="`Has ${parentCount} parent services`"
            >
              <i class="bi bi-diagram-3 me-1"></i>{{ parentCount }} parents
            </span>

            <!-- Disabled badge -->
            <span
              v-if="node.data.disabled"
              class="badge bg-warning-subtle text-warning-emphasis"
            >
              <i class="bi bi-power me-1"></i>Disabled
            </span>

            <!-- Per-service transaction outcome chips (history mode). Every service with run data shows
                 them, including a finished service with one transaction. A service still working shows
                 the row from the moment it starts, empty, so its first count climbs into place rather
                 than appearing. A finished service with no transactions shows a neutral "0". -->
            <!-- Keyed by the run, so switching to another one starts these counts over rather than
                 counting up to the new run's numbers from the old run's. -->
            <template v-if="node.data.run">
              <StatusCountChips
                :key="runId"
                :chips="serviceCountChips(node.data.run)"
                size="sm"
                :climbing="stillCounting"
              />
              <span
                v-if="finishedWithNoTransactions(node.data.run)"
                class="badge rounded-pill bg-secondary"
                title="No transactions"
              >0</span>
            </template>

            <!-- What the service is heading for, which the outcome chips cannot say. The figure is
                 projected from the parents read so far and revised as the service reads more. -->
            <small
              v-if="expected"
              class="text-body-tertiary"
              title="Projected from the parents read so far"
            >
              of {{ expected.toLocaleString() }}
            </small>
          </div>

          <!-- Permanent subtitle — service type, always muted -->
          <small v-if="node.data.subtitle" class="text-muted d-block text-truncate">
            {{ node.data.subtitle }}
          </small>

          <div v-if="expected" class="progress service-progress mt-1" role="progressbar">
            <div class="progress-bar" :style="{ width: `${percentDone.toFixed(2)}%` }"></div>
          </div>
        </div>

        <!-- Status + duration stack (history mode) — tick top-right, duration bottom-right -->
        <div
          v-if="node.data.run?.status"
          class="status-duration d-flex flex-column align-items-end gap-1"
        >
          <div class="status-indicator" :title="getRunStatusLabel(node.data.run?.status)">
            <i
              class="bi"
              :class="[statusClass, statusIcon]"
            ></i>
          </div>
          <small v-if="runDuration" class="run-duration text-body-tertiary d-flex align-items-center">
            <i class="bi bi-clock me-1"></i>{{ runDuration }}
          </small>
        </div>

        <!-- Action buttons (editor mode only) -->
        <div
          v-if="node.data.canvasMode === 'editor'"
          class="action-buttons d-none d-md-flex gap-1"
          @click.stop
        >
          <button
            class="bb-btn-secondary-cta btn-sm"
            title="Run service"
            @click="handleRunService"
          >
            <i class="bi bi-play-fill me-1"></i>Run
          </button>

          <!-- Dropdown for more options -->
          <div class="dropdown">
            <button
              v-dropdown-menu
              class="bb-btn-secondary-cta btn-sm dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              title="More options"
            >
              <i class="bi bi-three-dots"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <a class="dropdown-item" href="#" @click.prevent="handleRunService">
                  <i class="bi bi-play-fill me-2"></i>Run
                </a>
              </li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="handleAddService">
                  <i class="bi bi-plus-circle me-2"></i>Add Child Service
                </a>
              </li>
              <li>
                <a
                  class="dropdown-item"
                  href="#"
                  @click.prevent="handleToggleDisabled"
                >
                  <i
                    class="bi bi-power me-2"
                    :class="node.data.disabled ? 'text-success' : 'text-danger'"
                  ></i>
                  {{ node.data.disabled ? 'Enable' : 'Disable' }}
                </a>
              </li>
              <li><hr class="dropdown-divider" /></li>
              <li>
                <a
                  class="dropdown-item"
                  href="#"
                  @click.prevent="handleDuplicateService"
                >
                  <i class="bi bi-files me-2"></i>Duplicate
                </a>
              </li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="handleRenameService">
                  <i class="bi bi-pencil me-2"></i>Rename
                </a>
              </li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="handleEditParents">
                  <i class="bi bi-diagram-3 me-2"></i>Edit Parent Services
                </a>
              </li>
              <li><hr class="dropdown-divider" /></li>
              <li>
                <a
                  class="dropdown-item text-danger"
                  href="#"
                  @click.prevent="handleDeleteService"
                >
                  <i class="bi bi-trash me-2"></i>Delete
                </a>
              </li>
            </ul>
          </div>
        </div>

        <!-- Mobile action button -->
        <div
          v-if="node.data.canvasMode === 'editor'"
          class="d-md-none"
          @click.stop
        >
          <div class="dropdown">
            <button
              v-dropdown-menu
              class="bb-btn-secondary-cta btn-sm"
              type="button"
              data-bs-toggle="dropdown"
              title="More options"
            >
              <i class="bi bi-three-dots-vertical"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <a class="dropdown-item" href="#" @click.prevent="handleRunService">
                  <i class="bi bi-play-fill me-2"></i>Run
                </a>
              </li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="handleAddService">
                  <i class="bi bi-plus-circle me-2"></i>Add Child Service
                </a>
              </li>
              <li>
                <a
                  class="dropdown-item"
                  href="#"
                  @click.prevent="handleToggleDisabled"
                >
                  <i
                    class="bi bi-power me-2"
                    :class="node.data.disabled ? 'text-success' : 'text-danger'"
                  ></i>
                  {{ node.data.disabled ? 'Enable' : 'Disable' }}
                </a>
              </li>
              <li><hr class="dropdown-divider" /></li>
              <li>
                <a
                  class="dropdown-item"
                  href="#"
                  @click.prevent="handleDuplicateService"
                >
                  <i class="bi bi-files me-2"></i>Duplicate
                </a>
              </li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="handleRenameService">
                  <i class="bi bi-pencil me-2"></i>Rename
                </a>
              </li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="handleEditParents">
                  <i class="bi bi-diagram-3 me-2"></i>Edit Parent Services
                </a>
              </li>
              <li><hr class="dropdown-divider" /></li>
              <li>
                <a
                  class="dropdown-item text-danger"
                  href="#"
                  @click.prevent="handleDeleteService"
                >
                  <i class="bi bi-trash me-2"></i>Delete
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Expanded content — shared editor/run body, depending on mode -->
    <Transition name="expand">
      <div v-if="isExpanded" class="card-body pt-0" @click.stop="closeOpenMenus()">
        <ServiceEditor
          v-if="node.data.canvasMode === 'editor'"
          :model-value="service ?? null"
          :is-dirty="!!isDirty"
          @update:model-value="handleEditorUpdate"
          @save="handleEditorSave"
          @discard="handleEditorDiscard"
          @run="handleEditorRun"
          @toggle-component="handleEditorToggleComponent"
        />
        <template v-else-if="runId && runWorkflowId">
          <!-- What this service cost, in the panel that already opens rather than floating over one that
               does not — the card and the stack both clip their overflow, so anything hovering has to be
               lifted out of both, and nothing here is worth that. -->
          <details class="service-cost mb-3">
            <summary class="small text-body-tertiary">Stats for nerds</summary>
            <div class="pt-2">
              <RunCostRows :cost="node.data.run?.cost" :processing-ms="node.data.run?.processingMs" />
            </div>
          </details>
          <RunTransactionExplorer
            :run-id="runId"
            :service-id="node.id"
            :total="node.data.run?.total ?? 0"
          />
        </template>
      </div>
    </Transition>
    </div>
  </div>
</template>

<style scoped lang="scss">

/* Wrapper for the entire card + tree lines */
.stacked-service-wrapper {
  position: relative;
  margin-bottom: 0.75rem;
}

/* While this card's "more options" menu is open, lift it above later sibling cards
   so the downward menu paints on top instead of being hidden behind the card below. */
.stacked-service-wrapper.menu-open {
  z-index: 10;
}

.stacked-service-card {
  position: relative;
  background: var(--bs-body-bg);
}

/* A hairline, and no transition on the fill: the width is redrawn every frame by the number climbing
   beside it, and a CSS transition on top of that lags the figure it is illustrating. */
.service-progress {
  height: 3px;

  .progress-bar {
    transition: none;
  }
}

/* Duration shown bottom-right under the status tick — keep it visually quiet so
   the status icon reads as the primary indicator. */
.run-duration {
  font-size: 0.8rem;
  line-height: 1;
  opacity: 0.85;
}

/* Disabled state */
.stacked-service-card-disabled {
  opacity: 0.6;
  border-color: var(--bs-warning) !important;
  background-color: var(--bs-warning-bg-subtle) !important;
}

/* Expanded state */
.stacked-service-card-expanded {
  border-color: var(--bs-primary) !important;
}

/* Dirty state — subtle accent on the left */
.stacked-service-card-dirty {
  border-left: 3px solid var(--bs-warning) !important;
}

/* Service icon */
.service-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

[data-bs-theme='dark'] .service-icon {
  background-color: var(--bs-secondary-bg) !important;
  border-color: var(--bs-secondary-border-subtle) !important;
}

/* Expand button */
.expand-btn {
  min-width: 24px;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.expand-btn:hover {
  opacity: 1;
}

/* Action buttons */
.action-buttons {
  flex-shrink: 0;
}

/* Expand/collapse animation */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.expand-enter-to,
.expand-leave-from {
  max-height: 1000px;
  opacity: 1;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .stacked-service-card {
    font-size: 0.9rem;
  }

  .service-icon {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }

  /* Chevron closer to the card edge on mobile. !important to beat Bootstrap's p-3 utility. */
  .stacked-service-card .card-header {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
}
</style>
